import React from 'react';
import { motion } from 'framer-motion';
import { Countdown } from '../ui/Countdown';
import { trackInitiateCheckout } from '../MetaPixel';
import { Lock, Zap, ShieldCheck, CreditCard, Check } from 'lucide-react';
interface PricingSectionProps {
  id?: string;
  isReprise?: boolean;
}
type PackId = 'profesional';
const PACKS = {
  profesional: {
    id: 'profesional' as PackId,
    icon: '📘',
    name: 'PACK PROFESIONAL',
    description: '12 Tomos + 6 Bonus',
    priceOld: '$29 USD',
    price: '$19 USD',
    url: 'https://yoga360.impultienda.ar/checkout',
    cta: 'SÍ, QUIERO EL PACK PROFESIONAL →',
    includes: ['12 Tomos con secuencias detalladas, por nivel y objetivo (de inicial a avanzado, adultos mayores, prenatal, terapéutico y más)', '+280 páginas y +150 ejercicios detallados, listos para dictar', '6 Bonus estratégicos: Cancionero, Talleres, Instagram, Ajustes Verbales, Props y Anatomía Aplicada', 'Sistema "Abre y Dicta": fichas listas para imprimir y usar mañana', 'Acceso inmediato y de por vida — descarga en PDF las 24 hs', 'Garantía de 7 días: si no es lo que esperabas, te devolvemos el 100%']
  }
};
export function PricingSection({
  id,
  isReprise = false
}: PricingSectionProps) {
  const pack = PACKS.profesional;
  const handleCheckout = () => {
    trackInitiateCheckout(pack.id);
    window.location.href = pack.url;
  };
  return <section id={id} className="py-20 px-4 bg-oscuro relative overflow-hidden" data-id="element-142">
      <div className="max-w-3xl mx-auto relative z-10" data-id="element-143">
        {/* Countdown header */}
        <div className="text-center mb-10" data-id="element-144">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-4xl font-extrabold text-red-500 mb-6" data-id="element-145">
            {isReprise ? '⏰ El precio de lanzamiento cierra en:' : '⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO'}
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="mb-4" data-id="element-146">
            <Countdown data-id="element-147" />
          </motion.div>

          {!isReprise && <p className="text-white/80 text-base font-medium" data-id="element-148">
              🔴 El precio sube cuando termina el contador
            </p>}
        </div>

        {/* Checkout card */}
        <motion.div id="elegi-tu-pack" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-2xl shadow-2xl p-6 md:p-10 scroll-mt-6" data-id="element-149">
          <h3 className="text-2xl md:text-4xl font-extrabold text-oscuro text-center mb-2" data-id="element-150">
            Obtén tu Pack:
          </h3>
          <p className="text-center text-gray-500 mb-8 text-sm md:text-base" data-id="element-151">
            Incluye los 6 Bonus estratégicos
          </p>

          {/* Single pack */}
          <div className="rounded-2xl border-2 border-terracota bg-terracota/5 shadow-sm p-4 md:p-5 flex items-center gap-3 md:gap-4 mb-6" data-id="element-152">
            <div className="flex-1 min-w-0" data-id="element-153">
              <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-154">
                <span className="font-extrabold text-oscuro text-sm md:text-lg flex items-center gap-1.5" data-id="element-155">
                  <span data-id="element-156">{pack.icon}</span>
                  {pack.name}
                </span>
              </div>
              <p className="text-gray-500 text-xs md:text-sm" data-id="element-157">
                {pack.description}
              </p>
            </div>

            <div className="text-right flex-shrink-0" data-id="element-158">
              <p className="text-gray-400 line-through text-xs md:text-sm" data-id="element-159">
                {pack.priceOld}
              </p>
              <p className="font-extrabold text-lg md:text-2xl text-terracota" data-id="element-160">
                {pack.price}
              </p>
            </div>
          </div>

          {/* What you get */}
          <div className="bg-hueso rounded-2xl p-5 md:p-6 mb-6" data-id="element-161">
            <p className="font-bold text-oscuro text-sm md:text-base mb-4" data-id="element-162">
              Esto es todo lo que te llevas hoy:
            </p>
            <ul className="space-y-3" data-id="element-163">
              {pack.includes.map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-164">
                  <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-salvia/15 text-salvia flex items-center justify-center" data-id="element-165">
                    <Check size={13} strokeWidth={3} data-id="element-166" />
                  </span>
                  <span className="text-gray-700 text-sm md:text-[15px] leading-relaxed" data-id="element-167">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          <motion.button type="button" onClick={handleCheckout} whileTap={{
          scale: 0.98
        }} whileHover={{
          scale: 1.01
        }} className="w-full bg-terracota hover:bg-terracota-dark text-white font-extrabold py-5 px-6 rounded-2xl text-base md:text-xl shadow-lg shadow-terracota/30 transition-all duration-200" data-id="element-168">
            {pack.cta}
          </motion.button>

          <p className="text-center text-gray-400 italic text-xs md:text-sm mt-4" data-id="element-169">
            Acceso inmediato luego del pago
          </p>
        </motion.div>

        {/* Trust badges */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-170">
          {[{
          icon: <Lock size={18} data-id="element-171" />,
          label: 'Pago seguro'
        }, {
          icon: <Zap size={18} className="text-dorado" data-id="element-172" />,
          label: 'Acceso en 5 min'
        }, {
          icon: <ShieldCheck size={18} className="text-salvia" data-id="element-173" />,
          label: 'Garantía 7 días'
        }, {
          icon: <CreditCard size={18} data-id="element-174" />,
          label: 'Tarjeta · Transferencia'
        }].map((badge, idx) => <div key={idx} className="flex items-center justify-center md:justify-start gap-2 text-white/80 text-xs md:text-sm font-medium" data-id="element-175">
              <span className="text-white" data-id="element-176">{badge.icon}</span>
              {badge.label}
            </div>)}
        </div>
      </div>
    </section>;
}